import * as React from "react";
import { Lock } from "lucide-react";
import { Navigate, useLocation } from "react-router";
import { useMe, type MeResponse } from "@/api/me";
import { useAuthStore } from "@/store/authStore";

/**
 * Role + feature-aware route guard. Wraps any element that should only render
 * for specific Cognito groups, with optional feature gating layered on top.
 *
 * Usage in router config:
 *
 *   // Role-only:
 *   <RoleGate allow={["SuperAdmin", "ClientAdmin"]}>
 *     <AdminClientsPage />
 *   </RoleGate>
 *
 *   // Role + feature (MV2-186):
 *   <RoleGate
 *     allow={["SuperAdmin", "ClientAdmin", "User"]}
 *     requireFeature="subscribe_report"
 *   >
 *     <ReportsPage />
 *   </RoleGate>
 *
 *   // Role + any-of features:
 *   <RoleGate
 *     allow={["SuperAdmin", "ClientAdmin", "User"]}
 *     requireAnyFeature={["generate_report", "subscribe_report"]}
 *   >
 *     <ReportsPage />
 *   </RoleGate>
 *
 * Behaviour:
 *   * Not authenticated → redirect to /login with ?next=<current>.
 *   * /me hasn't loaded yet → render a tiny spinner placeholder.
 *   * Authenticated but cognito_group not in `allow` → render AccessDenied
 *     inline (not a redirect — direct-URL hit on a gated page shouldn't lose
 *     navigation history).
 *   * Role allowed but missing `requireFeature` → AccessDenied with a
 *     feature-specific copy. SuperAdmin always passes the feature check
 *     because their `/me.features` carries the full backend FEATURES set.
 */
export type Persona = "SuperAdmin" | "ClientAdmin" | "User";

interface RoleGateProps {
  allow: readonly Persona[];
  /**
   * When true, the gate also blocks SuperAdmin while operating with an
   * X-Acting-As-Client header — used for re:bot-internal surfaces
   * (MLOps in particular) that shouldn't leak into a tenant view. The
   * sidebar already hides these items during impersonation, but a
   * direct-URL hit (bookmark, history, manual address-bar) would
   * otherwise bypass that — defence-in-depth here closes the door.
   */
  hideDuringImpersonation?: boolean;
  /** MV2-186: require a single policy feature in addition to role. */
  requireFeature?: string;
  /**
   * MV2-186: require ANY of these features (OR semantics). Useful when a
   * surface unlocks for either of two adjacent features (e.g. Reports
   * tab gates on ``generate_report`` OR ``subscribe_report``).
   */
  requireAnyFeature?: readonly string[];
  /**
   * MV2-906 (canvas 06 `admin/AdminNoPermission`) — name the surface so a
   * persona outside `allow` reads "{surface} isn't available to you" with
   * a reason written for them, instead of the generic role sentence.
   * `surfaceReason` may be a function of the viewer's /me so the copy can
   * name their client ("As ClientAdmin of ACEL Energy you manage …").
   */
  surface?: string;
  surfaceReason?: string | ((me: MeResponse) => string);
  children: React.ReactNode;
}

export function RoleGate({
  allow,
  hideDuringImpersonation,
  requireFeature,
  requireAnyFeature,
  surface,
  surfaceReason,
  children,
}: RoleGateProps): React.ReactElement {
  const status = useAuthStore((s) => s.status);
  const isImpersonating = useAuthStore((s) => s.actingAs?.clientId != null);
  const location = useLocation();
  const { data, isLoading } = useMe({ enabled: status === "authenticated" });

  if (status === "unauthenticated") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (status === "loading" || isLoading || !data) {
    return <RoleGateLoading />;
  }
  if (!isAllowed(data, allow)) {
    if (surface) {
      return (
        <AccessDenied
          required={allow}
          actual={data.cognito_group}
          reason="platform-surface"
          surface={surface}
          surfaceReason={
            typeof surfaceReason === "function"
              ? surfaceReason(data)
              : surfaceReason
          }
        />
      );
    }
    return <AccessDenied required={allow} actual={data.cognito_group} />;
  }
  if (hideDuringImpersonation && isImpersonating) {
    return (
      <AccessDenied
        required={allow}
        actual="SuperAdmin (impersonating)"
        reason="impersonating"
      />
    );
  }
  if (!hasRequiredFeature(data, requireFeature, requireAnyFeature)) {
    return (
      <AccessDenied
        required={allow}
        actual={data.cognito_group}
        reason="missing-feature"
        missingFeature={
          requireFeature ?? requireAnyFeature?.join(" / ") ?? null
        }
      />
    );
  }
  return <>{children}</>;
}

function hasRequiredFeature(
  me: MeResponse,
  requireFeature: string | undefined,
  requireAnyFeature: readonly string[] | undefined,
): boolean {
  // No feature gate → trivially satisfied.
  if (
    !requireFeature &&
    (!requireAnyFeature || requireAnyFeature.length === 0)
  ) {
    return true;
  }
  // Pre-MV2-186 backend: features may be undefined. Until rollout
  // completes, fail-open for SuperAdmin (still role-gated) and
  // fail-closed for everyone else so a direct-URL hit can't bypass.
  // Once /me always populates features this conditional is a no-op.
  const features = me.features;
  if (!features) {
    return me.cognito_group === "SuperAdmin";
  }
  if (requireFeature && !features.includes(requireFeature)) return false;
  if (
    requireAnyFeature &&
    requireAnyFeature.length > 0 &&
    !requireAnyFeature.some((f) => features.includes(f))
  ) {
    return false;
  }
  return true;
}

function isAllowed(me: MeResponse, allow: readonly Persona[]): boolean {
  return allow.includes(me.cognito_group as Persona);
}

function RoleGateLoading(): React.ReactElement {
  return (
    <div
      role="status"
      aria-busy="true"
      className="flex items-center justify-center h-full w-full min-h-[12rem]"
    >
      <div className="h-6 w-6 rounded-full border-2 border-[color:var(--gray-300)] border-t-[color:var(--brand-primary)] animate-spin" />
    </div>
  );
}

interface AccessDeniedProps {
  required: readonly Persona[];
  actual: string;
  reason?: "impersonating" | "missing-feature" | "platform-surface";
  /** MV2-186: feature key the route required and the user lacks. */
  missingFeature?: string | null;
  /** MV2-906: the surface's name and the persona-specific reason. */
  surface?: string;
  surfaceReason?: string;
}

/**
 * Canvas 06 `noPermission()`: lock glyph in gray-400, h2 "{what} isn't
 * available to you", body at 48ch — one block, centred, 64px of air.
 */
export function AccessDenied({
  required,
  actual,
  reason,
  missingFeature,
  surface,
  surfaceReason,
}: AccessDeniedProps): React.ReactElement {
  return (
    <div
      role="status"
      className="flex h-full w-full flex-col items-center justify-center gap-[10px] px-[22px] py-16 text-center"
    >
      <span aria-hidden className="text-gray-400">
        <Lock size={22} strokeWidth={1.75} />
      </span>
      <h1 className="text-[15px] font-semibold text-gray-900">
        {reason === "platform-surface"
          ? `${surface} isn’t available to you`
          : reason === "impersonating"
            ? "Not available during impersonation"
            : reason === "missing-feature"
              ? "You don’t have access to this surface"
              : "You don’t have access to this page"}
      </h1>
      <p className="max-w-[48ch] text-[13px] leading-[1.5] text-gray-500">
        {reason === "platform-surface" ? (
          <>
            {surfaceReason ??
              `This is a platform page for ${required.join(" / ")}.`}
          </>
        ) : reason === "impersonating" ? (
          <>
            This page is a re:bot-internal surface and is hidden while you
            impersonate a tenant. Exit impersonation from the top banner to view
            it.
          </>
        ) : reason === "missing-feature" ? (
          <>
            Your policy doesn’t grant the{" "}
            <strong>{missingFeature ?? "required"}</strong> feature. Contact
            your administrator to update your policy if you need access.
          </>
        ) : (
          <>
            This page is restricted to <strong>{required.join(" / ")}</strong>.
            Your account is a<strong> {actual}</strong>. Contact your
            administrator if you think this is a mistake.
          </>
        )}
      </p>
    </div>
  );
}
